import Box from '@mui/material/Box';
import { func, number, object } from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';

const rootSx = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  margin: '-7px',
};

const selectionSx = {
  border: '1px dashed #fff',
  position: 'absolute',
  margin: '7px',
  cursor: 'move',
};

const overlayWrapperSx = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  margin: '7px',
  overflow: 'hidden',
  pointerEvents: 'none',
};

const overlaySx = {
  position: 'absolute',
  top: 0,
  left: 0,
  boxShadow: '0 0 1000px 1000px rgba(0,0,0,0.3)',
};

const handleBaseSx = {
  position: 'absolute',
  width: '14px',
  height: '14px',
  zIndex: 1,
  top: 0,
  left: 0,
};

const handleDirectionSx = {
  tl: { cursor: 'nw-resize' },
  t: { marginLeft: '-4px', cursor: 'n-resize' },
  tr: { cursor: 'ne-resize' },
  l: { cursor: 'w-resize' },
  r: { cursor: 'e-resize' },
  bl: { cursor: 'sw-resize' },
  b: { marginLeft: '-4px', cursor: 's-resize' },
  br: { cursor: 'se-resize' },
};

const squareBaseSx = {
  border: '1px solid #000',
  position: 'absolute',
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: '#fff',
};

const squareDirectionSx = {
  tl: { top: '-5px', left: '-5px', cursor: 'nw-resize' },
  t: { top: '-5px', marginLeft: '-5px', left: '50%', cursor: 'n-resize' },
  tr: { top: '-5px', right: '-5px', cursor: 'ne-resize' },
  l: { left: '-5px', marginTop: '-5px', top: '50%', cursor: 'w-resize' },
  r: { right: '-5px', marginTop: '-5px', top: '50%', cursor: 'e-resize' },
  bl: { bottom: '-5px', left: '-5px', cursor: 'sw-resize' },
  b: { bottom: '-5px', marginLeft: '-5px', left: '50%', cursor: 's-resize' },
  br: { bottom: '-5px', right: '-5px', cursor: 'se-resize' },
};

const direction = {
  t: 'y',
  b: 'y',
  r: 'x',
  l: 'x',
  tr: 'both',
  tl: 'both',
  br: 'both',
  bl: 'both',
};

const ResizableDraggable = ({
  imgWidth = 0,
  box = { top: 0, right: 0, bottom: 0, left: 0 },
  onResize,
}) => {
  const rectRef = useRef();
  const [rect, setRect] = useState(box);
  const [rootRef] = useState({ current: null });

  const handleStop = () => {
    const { width, height } = rootRef.current.parentNode.getBoundingClientRect();
    const scale = imgWidth ? imgWidth / width : 1;
    const recWidth = width - rect.left - rect.right;
    const recHeight = height - rect.top - rect.bottom;
    onResize({
      left: rect.left,
      top: rect.top,
      bottom: rect.bottom,
      right: rect.right,
      width: recWidth,
      height: recHeight,
      scale,
      aspectRatio: recWidth / recHeight,
    });
  };

  const handleDrag = dir => (e, d) => {
    switch (dir) {
      case 'tl':
        setRect(r => ({ ...r, top: r.top + d.deltaY, left: r.left + d.deltaX }));
        break;
      case 't':
        setRect(r => ({ ...r, top: r.top + d.deltaY }));
        break;
      case 'tr':
        setRect(r => ({ ...r, top: r.top + d.deltaY, right: r.right - d.deltaX }));
        break;
      case 'l':
        setRect(r => ({ ...r, left: r.left + d.deltaX }));
        break;
      case 'r':
        setRect(r => ({ ...r, right: r.right - d.deltaX }));
        break;
      case 'bl':
        setRect(r => ({ ...r, bottom: r.bottom - d.deltaY, left: r.left + d.deltaX }));
        break;
      case 'b':
        setRect(r => ({ ...r, bottom: r.bottom - d.deltaY }));
        break;
      case 'br':
        setRect(r => ({ ...r, bottom: r.bottom - d.deltaY, right: r.right - d.deltaX }));
        break;
      case 'selection':
        setRect(r => ({
          ...r,
          top: r.top + d.deltaY,
          left: r.left + d.deltaX,
          right: r.right - d.deltaX,
          bottom: r.bottom - d.deltaY,
        }));
        break;
      default:
    }
  };

  const getPosition = dir => {
    if (!rootRef.current) {
      return { x: 0, y: 0 };
    }
    const { width, height } = rootRef.current.parentNode.getBoundingClientRect();
    const recWidth = width - rect.left - rect.right;
    const recHeight = height - rect.top - rect.bottom;
    switch (dir) {
      case 'tl':
        return { x: rect.left, y: rect.top };
      case 't':
        return { x: recWidth / 2 + rect.left, y: rect.top };
      case 'tr':
        return { x: width - rect.right, y: rect.top };
      case 'l':
        return { x: rect.left, y: recHeight / 2 + rect.top };
      case 'r':
        return { x: width - rect.right, y: recHeight / 2 + rect.top };
      case 'bl':
        return { x: rect.left, y: height - rect.bottom };
      case 'b':
        return { x: recWidth / 2 + rect.left, y: height - rect.bottom };
      case 'br':
        return { x: width - rect.right, y: height - rect.bottom };
      case 'selection':
        return { top: 0, left: 0, width: recWidth, height: recHeight };
      case 'overlay':
        return { top: rect.top, left: rect.left, width: recWidth, height: recHeight };

      default:
        return () => {};
    }
  };

  useEffect(() => {
    handleStop();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset drag state on mount only
  }, []);

  return (
    <Box sx={rootSx} ref={rootRef}>
      {Object.entries(direction).map(([d, axis]) => {
        return (
          <Draggable
            position={getPosition(d)}
            axis={axis}
            bounds="parent"
            onDrag={handleDrag(d)}
            onStop={handleStop}
            key={d}
          >
            <Box sx={[handleBaseSx, handleDirectionSx[d]]} />
          </Draggable>
        );
      })}
      <Box sx={overlayWrapperSx}>
        <Box sx={overlaySx} style={getPosition('overlay')} />
      </Box>
      <Draggable
        bounds="parent"
        axis="both"
        position={{ x: rect.left, y: rect.top }}
        onDrag={handleDrag('selection')}
        onStop={handleStop}
        ref={rectRef}
      >
        <Box sx={selectionSx} style={getPosition('selection')}>
          {Object.keys(direction).map(d => {
            return <Box key={d} sx={[squareBaseSx, squareDirectionSx[d]]} />;
          })}
        </Box>
      </Draggable>
    </Box>
  );
};

ResizableDraggable.propTypes = {
  onResize: func.isRequired,
  box: object,
  imgWidth: number,
};

export default ResizableDraggable;
